import React, { Suspense, lazy } from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { i18nTranslate } from 'src/utils'
import { Modal, Button, Row } from 'react-bootstrap'
import {
  storeContainer,
  addressContainer,
  checkoutContainer,
  nlxxConfigContainer,
} from 'src/models'
import {
  getAddressFieldsJsonBasedOnCountry,
  getAddressOnlyFieldsForValidation,
  checkIfTwoFormValuesAreEqual,
  getValidationErrorMessage,
} from 'src/deps'
import { AddressValidation, toastState } from 'src/views/components'
import {
  removeShippingAddressFormValidationMeta,
  shouldMakeLegacyAddressValidationApiCall,
} from 'src/views/components/CommonFormForCS/shippingAddressFormValidation'
import './styles.scss'

@observer
class AddressEditModal extends React.Component {
  @observable isToShowAddressValidationModal = false
  @observable isProvidedAddressInvalid = false
  @observable recommendationAddressList = []

  currentMarket = storeContainer?.defaultShippingRegion
    ? 'Address' + storeContainer?.defaultShippingRegion
    : 'AddressUS'
  AddressForm = lazy(
    () =>
      import(
        'src/views/addresses/' + this.currentMarket + '/' + this.currentMarket
      )
  )

  componentDidMount() {
    // Load NLXX configuration on mount if not already loaded for current store
    if (
      !nlxxConfigContainer.nlxxConfigStoreId ||
      nlxxConfigContainer.nlxxConfigStoreId !== storeContainer.activeStoreId
    ) {
      nlxxConfigContainer.loadNLXXConfig()
    }
  }

  getFormFieldsBasedOnCountry = async () => {
    let editAddressFormFieldJson = await getAddressFieldsJsonBasedOnCountry(
      storeContainer?.defaultShippingRegion
    )
    let editAddressFormFields = await getAddressOnlyFieldsForValidation(
      editAddressFormFieldJson,
      'subscription'
    )
    return editAddressFormFields
  }

  handleToggleView = () => {
    checkoutContainer.isInvalidPhoneCountryCodeIN = false
    this.props.toggleSavedAddressEditModal()
  }

  handleAddressSuggestionModalContinue = async (formData = {}) => {
    const { handleEditSavedAddressContinue = function () {} } = this.props
    if (formData?.selectedIndex <= 0) {
      let response = await addressContainer.originalAddressApiCall(formData)
      this.handleAddressSuggestionModalClose()
      if (addressContainer.isSuccessResponse(response)) {
        this.handleToggleView()
        handleEditSavedAddressContinue(formData)
      } else {
        const errorMessage =
          getValidationErrorMessage(response) || response?.message || ''
        toastState.setToastMessage(errorMessage)
      }
    } else {
      this.handleAddressSuggestionModalClose()
      this.handleToggleView()
      handleEditSavedAddressContinue(formData)
    }
  }

  handleAddressSuggestionModalClose = () => {
    this.isToShowAddressValidationModal = false
  }

  handleEditAddressContinue = async (formData = {}, reqBody = {}) => {
    const isToMakeAddressValidationApi =
      shouldMakeLegacyAddressValidationApiCall(
        formData,
        storeContainer?.defaultShippingRegion
      )
    const cleanedFormData = removeShippingAddressFormValidationMeta(formData)
    const validationRequest = removeShippingAddressFormValidationMeta(
      Object.keys(reqBody || {}).length > 0 ? reqBody : formData
    )
    const transformedAddressData = {
      ...cleanedFormData,
      zip: cleanedFormData?.zip || cleanedFormData?.postalCode || '',
      setDefaultAddress: cleanedFormData?.setDefaultAddress ?? false,
      country: reqBody?.country || cleanedFormData?.country || '',
    }

    if (isToMakeAddressValidationApi) {
      const res = await addressContainer.validateAddress(validationRequest)
      const recommendationAddress =
        res?.address?.validationDetails?.[0]?.recommendations || []
      if (res?.code === 'ADCOVAL0003') {
        this.recommendationAddressList = [{ ...transformedAddressData }, []]
        this.isProvidedAddressInvalid = true
        this.isToShowAddressValidationModal = true
      } else if (res?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
        this.isToShowAddressValidationModal = false
        toastState.setToastMessage(
          i18nTranslate('address.NotAdded', 'Address not added')
        )
        return
      } else if (addressContainer.isSuccessResponse(res)) {
        const editAddressFormFields = await this.getFormFieldsBasedOnCountry()
        let suggestedFirstAddress = recommendationAddress?.[0] || {}
        let isFirstAddressSameAsCurrentAddress =
          await checkIfTwoFormValuesAreEqual(
            editAddressFormFields,
            transformedAddressData,
            suggestedFirstAddress
          )

        if (
          Object.keys(suggestedFirstAddress)?.length > 0 &&
          !isFirstAddressSameAsCurrentAddress
        ) {
          this.recommendationAddressList = [
            { ...transformedAddressData },
            ...recommendationAddress,
          ]
          this.isProvidedAddressInvalid = false
          this.isToShowAddressValidationModal = true
        } else {
          this.handleAddressSuggestionModalContinue(transformedAddressData)
        }
      } else {
        this.isToShowAddressValidationModal = false
        toastState.setToastMessage(
          i18nTranslate('address.NotAdded', 'Address not added')
        )
        return
      }
    } else {
      this.handleAddressSuggestionModalContinue(transformedAddressData)
    }
  }

  renderSubscriptionSubmitButton = () => {
    return (
      <Modal.Footer className="border-top saved-address-edit-tab-footer">
        <Row
          noGutters
          className="justify-content-end w-100 saved-address-edit-tab-buttons">
          <Button
            tabIndex={0}
            variant="outline-primary"
            className="saved-address-edit-close-button"
            onClick={this.handleToggleView}
            data-testid="qa-saved-address-edit-close">
            {' '}
            {i18nTranslate('shipping.closeButton', 'Close')}
          </Button>
          <Button
            tabIndex={0}
            type="submit"
            className="saved-address-edit-continue-button"
            variant="primary"
            disabled={checkoutContainer?.isInvalidPhoneCountryCodeIN}
            data-testid="qa-saved-address-edit-continue">
            {i18nTranslate('editShippingAddress.applyChanges', 'Apply Changes')}
          </Button>
        </Row>
      </Modal.Footer>
    )
  }

  renderEditAddressForm = AddressForm => {
    const { addressInfo = {} } = this.props
    const addressData = addressContainer.addressData || []

    return (
      <Suspense fallback={<div>Loading Address Form...</div>}>
        <AddressForm
          shippingAddresses={addressData}
          onSubmit={this.handleEditAddressContinue}
          renderSubmitButton={this.renderSubscriptionSubmitButton}
          type="subscription"
          addressInfo={{
            ...addressInfo,
            country: addressInfo.country,
          }}
        />
      </Suspense>
    )
  }

  render() {
    const { customModalTitle = '', isToShowSavedAddressEditModal = false } =
      this.props

    return (
      <>
        <Modal
          aria-hidden={false}
          centered
          data-testid="qa-multiple-subscription-edit-address-modal"
          show={isToShowSavedAddressEditModal}
          key="address-edit-modal"
          className="subscription-saved-address-edit-modal"
          onHide={this.handleToggleView}>
          <Modal.Header closeButton>
            <Modal.Title
              as="h4"
              className="mb-0 subscription-saved-address-edit-modal-title"
              data-testid="qa-subscription-edit-address-modal-title">
              {customModalTitle}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            className="subscription-saved-address-edit-modal-body"
            data-testid="qa-subscription-edit-address-modal-body">
            {this.renderEditAddressForm(this.AddressForm)}
          </Modal.Body>
        </Modal>
        {this.isToShowAddressValidationModal && (
          <AddressValidation
            open={this.isToShowAddressValidationModal}
            addressInvalidFlag={this.isProvidedAddressInvalid}
            recommendations={this.recommendationAddressList}
            handleClose={this.handleAddressSuggestionModalClose}
            handleClickOnPro={this.handleAddressSuggestionModalContinue}
          />
        )}
      </>
    )
  }
}

export { AddressEditModal }
export default AddressEditModal
